/* eslint-disable prettier/prettier */
import {
  ActionIcon,
  Avatar,
  Badge,
  Anchor,
  MantineThemeColorsOverride,
  MantineThemeOverride,
  Pagination,
  Select,
  Textarea,
  createTheme,
  TextInput,
  Autocomplete,
  Text,
  DEFAULT_THEME,
  mergeMantineTheme,
  Loader,
  MultiSelect,
  Chip,
  TagsInput,
  Checkbox,
  Button,
  SegmentedControl,
  Tooltip,
  NumberInput,
  ColorInput,
  FileInput,
  Card,
  Tabs,
  Switch,
  Accordion,
} from "@mantine/core"
import { DateInput, DatePicker } from "@mantine/dates"
import "@mantine/dates/styles.css"
import "@mantine/dropzone/styles.css"

import classes from "./css/Global.module.css"
import { variantColorResolver } from "./variantResolver"

/**
 * Never export it!
 * Prefer using `const { colors } = useMantineTheme()`
 */
const colors: Partial<MantineThemeColorsOverride["colors"]> = {
  green: [
    "#f1ffeb",
    "#e1fed4",
    "#bffea2",
    "#9bfe6d",
    "#7efe44",
    "#6bfe2f",
    "#61fe25",
    "#51e21c",
    "#45c913",
    "#39b800", // this is the color in chartre graphique 9
  ],
  sand: [
    "#faf4e5", // this is the color in chartre graphique 0
    "#f5eedd",
    "#eddcb4",
    "#e4c887",
    "#dcb862",
    "#d7ad4b",
    "#d5a83e",
    "#bd9330",
    "#a88228",
    "#91701c",
  ],
  blue: [
    "#e6f0ff",
    "#cdddff",
    "#9ab7ff",
    "#638fff",
    "#4679ff",
    "#1858ff",
    "#014dff",
    "#003ee5",
    "#0037ce",
    "#002fb6",
  ],
  ground: [
    "#f6f5f4",
    "#e8e8e8",
    "#cecece",
    "#b4b3b2",
    "#9e9c99",
    "#918d89",
    "#8b857f",
    "#79726c",
    "#6c655e",
    "#1f1c19", // this is the color in chartre graphique 9
  ],
  ice: [
    "#ecf6fe",
    "#d7e4ee", // this is the color in chartre graphique 1
    "#bcd0df",
    "#97b5cd",
    "#789fbe",
    "#6491b5",
    "#588ab2",
    "#48779d",
    "#3c6a8d",
    "#2b5c7e",
  ],
  red: [
    "#ffece4",
    "#ffd8cd",
    "#ffaf9b",
    "#fe8466",
    "#fc5f38",
    "#fc4315", // this is the color in chartre graphique 5
    "#fd3b0a",
    "#e12c00",
    "#c92400",
    "#b01900",
  ],
  orange: [
    "#fff5e2",
    "#ffe9cd",
    "#ffd19c",
    "#feb766",
    "#fea239",
    "#fe941d",
    "#fe8d0e",
    "#f58300", // this is the color in chartre graphique 7
    "#ca6b00",
    "#b05b00",
  ],
  gray: [
    "#f9f9f9",
    "#e7e7e7",
    "#cdcdcd",
    "#b2b2b2",
    "#9a9a9a",
    "#8b8b8b",
    "#848484",
    "#717171",
    "#656565",
    "#575757",
  ],
  yellow: [
    "#fff5e1",
    "#ffe8cc",
    "#ffd19a",
    "#ffbc6f",
    "#fea137",
    "#fe941b",
    "#ff8d09",
    "#e37900",
    "#cb6b00",
    "#b15b00",
  ],
  purple: [
    "#fce8ff",
    "#f0ceff",
    "#dd9bff",
    "#c964ff",
    "#b836ff",
    "#b01fff", // this is the color in chartre graphique 5
    "#a905ff",
    "#9300e5",
    "#8300cd",
    "#7200b4",
  ],
  walnut: [
    "#fbf4ef",
    "#eae7e5",
    "#d0cdca",
    "#b7b1ad",
    "#a19994",
    "#948a83",
    "#8f827a",
    "#7c7067",
    "#6f6259", // this is the color in chartre graphique 8
    "#645449",
  ],
  primary: [
    "#e6f0ff",
    "#cdddff",
    "#9ab7ff",
    "#638fff",
    "#4679ff",
    "#1858ff",
    "#014dff",
    "#003ee5",
    "#0037ce",
    "#002fb6",
  ],
  secondary: [
    "#f1f4f8",
    "#e2e6ea",
    "#c1cbd7",
    "#9daec5",
    "#7f95b5",
    "#6b86ab",
    "#607ea8",
    "#506c93",
    "#456084",
    "#263951", // this is the color in chartre graphique
  ],
}

export type Color = keyof typeof colors

const fontFamily =
  "Borna, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

export const themeOverride: MantineThemeOverride = createTheme({
  // Colors
  variantColorResolver,
  colors: {
    ...colors,
    primary: colors.primary,
    secondary: colors.secondary,
    admin: colors.purple,
  },
  primaryShade: {
    light: 4,
    dark: 4,
  },
  black: "#000",
  // Fonts
  fontFamily,
  autoContrast: true,
  spacing: {
    xxs: "0.25rem",
    xs: "0.5rem",
    sm: "0.75rem",
    md: "0.875rem",
    lg: "1rem",
    xl: "2rem",
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.5rem",
  },
  headings: {
    fontFamily,
    sizes: {
      h1: {
        fontSize: "1.5rem",
      },
      h2: {
        fontSize: "1.25rem",
      },
      h3: {
        fontSize: "1rem",
      },
      h4: {
        fontSize: "0.875rem",
      },
    },
  },
  other: {
    widths: {
      tooltip: {
        default: "18.75rem",
        m: "15.625rem",
      },
      card: {
        width: {
          default: "18rem",
          sm: "14rem",
          md: "18rem",
          lg: "20rem",
          xl: "25rem",
        },
        height: {
          default: "18.75rem",
          sm: "15.625rem",
          md: "25rem",
          lg: "32.8125rem",
        },
        minWidth: {
          default: "18rem",
          sm: "15rem",
          md: "18rem",
          lg: "20rem",
          xl: "30rem",
        },
      },
      grid: {
        span: {
          base: 12,
          sm: 6,
          xs: 12,
          md: 4,
          lg: 3,
          xl: 3,
        },
      },
    },
    table: {
      headers: {
        size: 150,
        minSize: 100,
        maxSize: 200,
      },
      cells: {
        size: 150,
        minSize: 100,
        maxSize: 200,
        checkbox: 40,
        action: 60,
      },
    },
    icon: {
      size: 16,
      stroke: 2,
    }
  },
  components: {
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        variant: "filled",
        size: "sm",
        styles: {
          input: {
            fontSize: "var(--mantine-font-size-xs)",
          },
        },
        classNames: {
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    Avatar: Avatar.extend({
      defaultProps: {
        variant: "filled",
        // color: "var(--mantine-color-text)",
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        underline: "hover",
        c: "var(--mantine-color-text)",
      },
    }),
    Card: Card.extend({
      defaultProps: {
        miw: "15rem",
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        size: "xs",
      },
      classNames: {
        input: classes.input,
        label: classes.label,
      },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        size: "xs",
        classNames: {
          input: classes.input,
          label: classes.label,
          control: classes.control,
        },
      },
    }),
    FileInput: FileInput.extend({
      defaultProps: {
        size: "xs",
        classNames: {
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    ColorInput: ColorInput.extend({
      defaultProps: {
        size: "xs",
      },
      classNames: {
        input: classes.input,
        label: classes.label,
      },
    }),
    DateInput: DateInput.extend({
      defaultProps: {
        classNames: {
          input: classes.input,
          label: classes.label,
        },
        size: "xs",
      },
    }),
    DatePicker: DatePicker.extend({
      defaultProps: {
        size: "xs",
      },
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: "secondary",
      },
    }),
    Button: Button.extend({
      defaultProps: {
        variant: "primary",
        size: "xs",
        p: "compact-xs",
        loaderProps: {
          type: "dots",
        },
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        styles: {
          root: {
            "--tab-border-color":
              colors.ice?.[1] || "var(--mantine-color-ice-0)",
          },
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        classNames: {
          input: classes.input,
          label: classes.label,
        },
        size: "xs",
      },
    }),
    Select: Select.extend({
      defaultProps: {
        size: "xs",
        classNames: {
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    Pagination: Pagination.extend({
      defaultProps: {
        size: "xs",
        radius: "sm",
      },
    }),
    Text: Text.extend({
      defaultProps: {
        className: classes.text,
        size: "sm",
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        variant: "light",
        py: 2,
        px: "xs",
        size: "xs",
        fz: "xs",
        autoContrast: true,
        radius: "sm",
      },
    }),

    Loader: Loader.extend({
      defaultProps: {
        type: "bars",
        size: "sm",
      },
    }),
    Chip: Chip.extend({
      defaultProps: {
        classNames: {
          label: classes.chip,
        },
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        size: "sm",
        classNames: classes,
      },
    }),
    TagsInput: TagsInput.extend({
      defaultProps: {
        size: "sm",
        className: classes.tagInput,
        classNames: {
          pill: classes.pill,
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        size: "sm",

        classNames: {
          input: classes.input,
          label: classes.label,
          pill: classes.pill,
        },
      },
    }),
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false,
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        size: "sm",
        color: "primary",
        bg: "white",
        autoContrast: true,
        withItemsBorders: false,
      },
    }),
    Accordion: Accordion.extend({
      defaultProps: {
        classNames: {
          control: classes.control,
        },
      },
    }),
    Tooltip: Tooltip.extend({
      styles: (theme) => ({
        tooltip: {
          maxWidth: theme.other.widths.tooltip.default,
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      }),
    }),
  },
})

export const theme = mergeMantineTheme(DEFAULT_THEME, themeOverride)
